import { type RefObject, useEffect } from "react";

type UseSlideoutFocusOptions = {
  isOpen: boolean;
  panelRef: React.RefObject<HTMLElement | null>;
  previouslyFocusedElementRef: RefObject<Element | null>;
};

/**
 * This hook manages focus for a slideout panel component.
 *
 * When the panel opens, it focuses the panel element.
 *
 * When the panel closes, it restores focus to the previously focused element.
 */
export function useSlideoutFocus({
  isOpen,
  panelRef,
  previouslyFocusedElementRef,
}: UseSlideoutFocusOptions) {
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;

      setTimeout(() => {
        if (panelRef.current) {
          panelRef.current.focus();
        }
      }, 100);
    } else {
      if (
        previouslyFocusedElementRef.current &&
        "focus" in previouslyFocusedElementRef.current
      ) {
        (previouslyFocusedElementRef.current as HTMLElement).focus();
      }
    }
  }, [isOpen, panelRef, previouslyFocusedElementRef]);
}
